/**
 * feel-bundle.js
 * Bundle builder logic for /pages/bundle
 * Source: _mockups/01_ALL_HTML_MOCKUPS/bundle.html (lines 2506–2906)
 *
 * Discount approach: Option A — Shopify Automatic Discounts
 *   - 2 items → 10% off, 3 items → 15% off, 4+ items → 20% off
 *   - Set up automatic discount rules in Shopify Admin → Discounts
 *   - This page shows THEORETICAL discounted prices for UX feedback
 *   - Actual discount applied at checkout by Shopify automatically
 *
 * Subscribe plan: passes selling_plan ID per product (from data-selling-plan-id)
 *   Appstle handles the subscription + sub discount at checkout
 */

(function () {
  'use strict';

  const S = window.FA_BUNDLE_STRINGS || {};

  const TIERS = [
    { qty: 0,  discount: 0,    label: 'Add 2 items for 10% off' },
    { qty: 2,  discount: 0.10, label: 'You unlocked 10% off — add 1 more for 15%' },
    { qty: 3,  discount: 0.15, label: 'You unlocked 15% off — add 1 more for 20%' },
    { qty: 4,  discount: 0.20, label: 'Maximum savings unlocked — 20% off' },
  ];

  const SUB_DISCOUNT = 0.15; // additional 15% for subscribe plan

  const fmt = (n) => '€' + n.toFixed(2).replace('.', ',');

  // ── DOM refs ──────────────────────────────────────────────────────────────
  const products    = Array.from(document.querySelectorAll('.bundle-product[data-bundle-item]'));
  const summaryItems     = document.querySelector('[data-summary-items]');
  const summarySubtotalEl  = document.querySelector('[data-subtotal]');
  const summaryDiscountEl  = document.querySelector('[data-savings]');
  const summaryDiscountRow = document.querySelector('[data-discount-row]');
  const summarySubDiscEl   = document.querySelector('[data-sub-savings]');
  const summarySubDiscRow  = document.querySelector('[data-sub-discount-row]');
  const summaryTotalEl     = document.querySelector('[data-total]');
  const checkoutBtn        = document.querySelector('[data-checkout]');
  const checkoutPriceEl    = document.querySelector('[data-checkout-price]');
  const mobileBar          = document.querySelector('[data-mobile-bar]');
  const mobileBarCount     = document.querySelector('[data-mbb-count]');
  const mobileBarDiscount  = document.querySelector('[data-mbb-discount]');
  const mobileBarTotal     = document.querySelector('[data-mbb-total]');
  const mobileBarBtn       = document.querySelector('[data-mbb-toggle]');
  const countEl            = document.querySelector('[data-bs-count]');
  const targetEl           = document.querySelector('[data-bs-target]');
  const slotsEl            = document.querySelector('[data-bs-slots]');
  const tierBtns           = Array.from(document.querySelectorAll('[data-bs-tier-btn]'));
  const planOptions        = Array.from(document.querySelectorAll('[data-bs-plan-option]'));
  const planInputs         = Array.from(document.querySelectorAll('[data-bs-plan-input]'));
  const planPriceOnce      = document.querySelector('[data-bs-plan-price-once]');
  const planPriceSub       = document.querySelector('[data-bs-plan-price-sub]');
  const planPriceSubOrig   = document.querySelector('[data-bs-plan-price-sub-original]');
  const heroAnchorSteps    = Array.from(document.querySelectorAll('[data-hero-step]'));

  if (!products.length) return;

  // ── State ─────────────────────────────────────────────────────────────────
  const state = new Map();
  products.forEach((p) => {
    const id = p.dataset.id || Math.random().toString(36).slice(2);
    if (!p.dataset.id) p.dataset.id = id;

    // Remove HTML hidden from stepper — CSS handles visibility via [data-selected]
    const stepper = p.querySelector('.qty-stepper');
    if (stepper && stepper.hasAttribute('hidden')) stepper.removeAttribute('hidden');

    state.set(id, {
      id,
      el: p,
      name: p.querySelector('.product-title')?.textContent.trim() || S.product_fallback || 'Product',
      price: parseFloat(p.dataset.price || '0'),
      bg: p.querySelector('.bp-image')?.getAttribute('style') || '',
      imageUrl: p.dataset.productImage || '',
      variantId: p.dataset.variantId || null,
      sellingPlanId: p.dataset.sellingPlanId || null,
      qty: 0,
      selected: false,
    });
  });

  let targetTier = 4;  // 2 | 3 | 4
  let plan = 'sub';    // 'once' | 'sub'

  // ── Helpers ───────────────────────────────────────────────────────────────
  function getTier(totalQty) {
    let current = TIERS[0];
    for (const t of TIERS) if (totalQty >= t.qty) current = t;
    return current;
  }

  function bgColor(bg) {
    if (!bg) return '';
    const m = bg.match(/background\s*:\s*([^;]+)/i);
    return m ? m[1].trim() : bg;
  }

  // ── Render helpers ────────────────────────────────────────────────────────
  function renderSlots(items, totalQty) {
    if (!slotsEl) return;
    const slotCount = Math.max(targetTier, totalQty);
    slotsEl.dataset.tierTarget = String(Math.min(slotCount, 4));

    const flat = [];
    items.forEach((i) => { for (let k = 0; k < i.qty; k++) flat.push(i); });

    let html = '';
    for (let i = 0; i < slotCount; i++) {
      const item = flat[i];
      if (item) {
        const color = bgColor(item.bg) || 'var(--cream)';
        const slotInner = item.imageUrl
          ? `<img class="bs-slot-img" src="${item.imageUrl}" alt="${item.name}" loading="lazy">`
          : `<div class="bs-slot-fill" style="background:${color}"></div>`;
        html += `
          <div class="bs-slot" data-filled="true" data-bs-slot-item="${item.id}">
            ${slotInner}
            <span class="bs-slot-name">${item.name.split(' ')[0]}</span>
            <button type="button" class="bs-slot-remove"
                    data-bs-slot-remove="${item.id}"
                    aria-label="${(S.remove_aria || 'Remove [name]').replace('[name]', item.name)}">&times;</button>
          </div>`;
      } else {
        html += `
          <div class="bs-slot" data-filled="false">
            <span class="bs-slot-empty-num">${i + 1}</span>
          </div>`;
      }
    }
    slotsEl.innerHTML = html;
  }

  function renderTierChips(totalQty) {
    tierBtns.forEach((btn) => {
      const t = parseInt(btn.dataset.bsTierBtn, 10);
      const isActive   = t === targetTier;
      const isReached  = totalQty >= t;
      const isCurrent  = (t === 2 && totalQty >= 2 && totalQty < 3)
                      || (t === 3 && totalQty >= 3 && totalQty < 4)
                      || (t === 4 && totalQty >= 4);
      btn.dataset.active  = isActive  ? 'true' : 'false';
      btn.dataset.reached = isReached ? 'true' : 'false';
      btn.dataset.current = isCurrent ? 'true' : 'false';
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  function renderHeroAnchor(totalQty) {
    heroAnchorSteps.forEach((step) => {
      const t = parseInt(step.dataset.heroStep, 10);
      const isCurrent = (t === 2 && totalQty >= 2 && totalQty < 3)
                     || (t === 3 && totalQty >= 3 && totalQty < 4)
                     || (t === 4 && totalQty >= 4);
      step.dataset.current = isCurrent ? 'true' : 'false';
    });
  }

  function renderStatus(totalQty) {
    const el = document.querySelector('[data-bs-status]');
    if (!el) return;
    let html;
    if (totalQty === 0)      html = S.status_0 || 'Pick <strong>2</strong> to unlock <strong>10%</strong> off';
    else if (totalQty === 1) html = S.status_1 || 'Pick <strong>1 more</strong> to unlock <strong>10%</strong> off';
    else if (totalQty === 2) html = S.status_2 || '<strong>10%</strong> unlocked — pick <strong>1 more</strong> for <strong>15%</strong>';
    else if (totalQty === 3) html = S.status_3 || '<strong>15%</strong> unlocked — pick <strong>1 more</strong> for <strong>20%</strong>';
    else                     html = S.status_4 || '<strong>20%</strong> unlocked — maximum savings';
    el.innerHTML = html;
  }

  function renderPlanToggle(subtotalAfterBundle) {
    planOptions.forEach((opt) => {
      const isChecked = opt.dataset.bsPlanOption === plan;
      opt.dataset.checked = isChecked ? 'true' : 'false';
      const input = opt.querySelector('input');
      if (input) input.checked = isChecked;
    });
    if (planPriceOnce) planPriceOnce.textContent = fmt(subtotalAfterBundle);
    const subPrice = subtotalAfterBundle * (1 - SUB_DISCOUNT);
    if (planPriceSub)    planPriceSub.textContent    = fmt(subPrice);
    if (planPriceSubOrig) planPriceSubOrig.textContent = fmt(subtotalAfterBundle);
  }

  // ── Main update ───────────────────────────────────────────────────────────
  function update() {
    const items    = Array.from(state.values()).filter((s) => s.selected && s.qty > 0);
    const totalQty = items.reduce((sum, i) => sum + i.qty, 0);
    const subtotal = items.reduce((sum, i) => sum + i.price * i.qty, 0);
    const tier     = getTier(totalQty);
    const bundleDiscount = subtotal * tier.discount;
    const afterBundle    = subtotal - bundleDiscount;
    const subDiscount    = plan === 'sub' ? afterBundle * SUB_DISCOUNT : 0;
    const total          = afterBundle - subDiscount;

    // Auto-bump target tier when user adds beyond current target
    if (totalQty > targetTier && totalQty <= 4) targetTier = totalQty;

    // Counter
    if (countEl)  countEl.textContent  = totalQty;
    if (targetEl) targetEl.textContent = targetTier >= 4 ? '4+' : String(targetTier);

    // Slots + tier chips + hero anchor + status copy + plan toggle
    renderSlots(items, totalQty);
    renderTierChips(totalQty);
    renderHeroAnchor(totalQty);
    renderStatus(totalQty);
    renderPlanToggle(afterBundle);

    // Accessible items list
    if (summaryItems) {
      summaryItems.innerHTML = items.length === 0
        ? ''
        : items.map((i) => `<span>${(S.item_summary || '[name], qty [qty], [price]').replace('[name]', i.name).replace('[qty]', i.qty).replace('[price]', fmt(i.price * i.qty))}</span>`).join(' · ');
    }

    // Totals
    if (summarySubtotalEl) summarySubtotalEl.textContent = fmt(subtotal);
    if (summaryDiscountEl) summaryDiscountEl.textContent  = '−' + fmt(bundleDiscount);
    if (summaryDiscountRow) summaryDiscountRow.style.display = bundleDiscount > 0 ? '' : 'none';
    if (summarySubDiscEl)  summarySubDiscEl.textContent   = '−' + fmt(subDiscount);
    if (summarySubDiscRow) summarySubDiscRow.hidden = !(plan === 'sub' && subDiscount > 0);
    if (summaryTotalEl)    summaryTotalEl.textContent     = fmt(total);

    // Checkout button
    if (checkoutBtn) {
      const enabled = totalQty >= 2;
      checkoutBtn.disabled = !enabled;
      if (checkoutPriceEl) {
        checkoutPriceEl.textContent = enabled ? '· ' + fmt(total) : '';
        if (checkoutBtn.childNodes[0]) {
          checkoutBtn.childNodes[0].textContent = enabled
            ? (S.cta_add || 'Add bundle to cart') + ' '
            : (S.cta_min || 'Add at least 2 products');
        }
      }
    }

    // Mobile sticky bar
    if (mobileBar) {
      const visible = totalQty > 0;
      mobileBar.dataset.visible = visible ? 'true' : 'false';
      if (mobileBarCount)    mobileBarCount.textContent    = totalQty;
      if (mobileBarDiscount) mobileBarDiscount.textContent = Math.round(tier.discount * 100) + '%';
      if (mobileBarTotal)    mobileBarTotal.textContent    = fmt(total);
    }
  }

  // ── setQty ────────────────────────────────────────────────────────────────
  function setQty(id, qty) {
    const s = state.get(id);
    if (!s) return;
    s.qty      = Math.max(0, Math.min(6, qty));
    s.selected = s.qty > 0;
    s.el.dataset.selected = s.selected ? 'true' : 'false';
    const qtyValueEl = s.el.querySelector('.qty-stepper .qty-value');
    if (qtyValueEl) qtyValueEl.textContent = s.qty;
    const toggleBtn = s.el.querySelector('.bp-toggle');
    if (toggleBtn) toggleBtn.textContent = s.selected ? (S.added || '✓ Added') : (S.add || '+ Add');
    update();
  }

  // ── Wire up product cards ─────────────────────────────────────────────────
  products.forEach((p) => {
    const id       = p.dataset.id;
    const toggleBtn = p.querySelector('.bp-toggle');
    const minus    = p.querySelector('[data-qty="minus"]');
    const plus     = p.querySelector('[data-qty="plus"]');

    if (toggleBtn) {
      toggleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const s = state.get(id);
        if (!s.selected) setQty(id, 1);
        else setQty(id, 0);
      });
    }
    if (minus) minus.addEventListener('click', (e) => { e.preventDefault(); const s = state.get(id); setQty(id, s.qty - 1); });
    if (plus)  plus.addEventListener('click',  (e) => { e.preventDefault(); const s = state.get(id); setQty(id, s.qty + 1); });
  });

  // ── Wire up tier selector ─────────────────────────────────────────────────
  tierBtns.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      targetTier = parseInt(btn.dataset.bsTierBtn, 10);
      update();
    });
  });

  // ── Wire up plan toggle ───────────────────────────────────────────────────
  planInputs.forEach((input) => {
    input.addEventListener('change', () => {
      if (input.checked) { plan = input.value; update(); }
    });
  });
  planOptions.forEach((opt) => {
    opt.addEventListener('click', (e) => {
      if (e.target.tagName === 'INPUT') return;
      const input = opt.querySelector('input');
      if (input && !input.checked) { input.checked = true; plan = input.value; update(); }
    });
  });

  // ── Slot remove (event delegation) ───────────────────────────────────────
  if (slotsEl) {
    slotsEl.addEventListener('click', (e) => {
      const rem = e.target.closest('[data-bs-slot-remove]');
      if (rem) {
        e.preventDefault();
        const s = state.get(rem.dataset.bsSlotRemove);
        if (s) setQty(s.id, Math.max(0, s.qty - 1));
      }
    });
  }

  // ── Mobile bar → scroll to summary ───────────────────────────────────────
  if (mobileBarBtn) {
    mobileBarBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const summary = document.querySelector('.builder-summary');
      if (summary) summary.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  // ── Checkout → add to cart via /cart/add.js ───────────────────────────────
  if (checkoutBtn) {
    checkoutBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      if (checkoutBtn.disabled || checkoutBtn.dataset.loading === 'true') return;

      const items = Array.from(state.values()).filter((s) => s.selected && s.qty > 0 && s.variantId);
      if (items.length === 0) return;

      // Build cart items payload
      const cartItems = items.map((s) => {
        const item = { id: parseInt(s.variantId, 10), quantity: s.qty };
        if (plan === 'sub' && s.sellingPlanId) {
          item.selling_plan = parseInt(s.sellingPlanId, 10);
        }
        return item;
      });

      // Loading state
      checkoutBtn.dataset.loading = 'true';
      checkoutBtn.disabled = true;
      const originalHTML = checkoutBtn.innerHTML;
      checkoutBtn.textContent = S.cta_adding || 'Adding to cart…';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: cartItems }),
        });

        if (!res.ok) throw new Error('Cart add failed: ' + res.status);

        // Open cart drawer (theme.liquid mounts it as window.cartDrawer or custom event)
        if (typeof window.cartDrawer !== 'undefined' && window.cartDrawer.open) {
          window.cartDrawer.open();
        } else {
          // Fallback: dispatch event that feel-cart-drawer.js listens for
          document.dispatchEvent(new CustomEvent('feel:cart:open'));
        }

        // Trigger cart count update
        document.dispatchEvent(new CustomEvent('feel:cart:updated'));

      } catch (err) {
        console.error('[feel-bundle] cart add error:', err);
        checkoutBtn.innerHTML = originalHTML;
      } finally {
        checkoutBtn.dataset.loading = 'false';
        // Re-enable after slight delay so user sees feedback
        setTimeout(() => {
          const items2 = Array.from(state.values()).filter((s) => s.selected && s.qty > 0);
          checkoutBtn.disabled = items2.reduce((sum, i) => sum + i.qty, 0) < 2;
        }, 1200);
      }
    });
  }

  // ── Initial render ────────────────────────────────────────────────────────
  update();

})();
